/*
 * Copyright 2026 Sony Corporation
 */
import type { AdminUserUsage, Guideline, ListUsageResponse } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import {
  formatPercent,
  formatPeriod,
  formatTokens,
  formatUsd,
  recentPeriods,
  tokensToApps,
  tokensToUsd,
} from '../services/usage';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

/** Limits are entered in millions of tokens: the numbers a person compares. */
const MILLION = 1_000_000;
const toMillions = (tokens: number): string => String(Number((tokens / MILLION).toFixed(2)));

const Figure = ({ label, value, detail }: { label: string; value: string; detail?: string }) => (
  <Paper sx={{ p: 2, flex: 1, minWidth: 160 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
    {detail && (
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    )}
  </Paper>
);

/** The limit cell: the limit in millions of tokens, editable, with what it means in dollars and apps. */
const LimitEditor = ({
  user,
  guideline,
  defaultLimit,
  onSave,
}: {
  user: AdminUserUsage;
  guideline: Guideline;
  defaultLimit: number;
  onSave: (limit: number | null) => Promise<void>;
}) => {
  const [value, setValue] = useState(toMillions(user.limit_tokens));
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(toMillions(user.limit_tokens)), [user.limit_tokens]);

  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100_000;
  const tokens = valid ? Math.round(parsed * MILLION) : user.limit_tokens;
  const changed = valid && tokens !== user.limit_tokens;
  const label = user.email ?? user.user_id;

  const save = async (limit: number | null) => {
    setSaving(true);
    try {
      await onSave(limit);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          type="number"
          value={value}
          error={!valid}
          onChange={(event) => setValue(event.target.value)}
          slotProps={{
            htmlInput: { min: 0, step: 1, 'aria-label': `Monthly limit of ${label} in millions of tokens` },
            input: { endAdornment: <Typography variant="caption">M</Typography> },
          }}
          sx={{ width: 120 }}
        />
        <Button size="small" variant="contained" disabled={!changed || saving} onClick={() => void save(tokens)}>
          Save
        </Button>
        {user.limit_custom ? (
          <Button size="small" disabled={saving} onClick={() => void save(null)}>
            Use default
          </Button>
        ) : (
          <Chip size="small" label="default" variant="outlined" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        ≈ {formatUsd(tokensToUsd(tokens, guideline))} · ≈ {tokensToApps(tokens, guideline)} apps a month
        {user.limit_custom ? '' : ` (default ${formatTokens(defaultLimit)})`}
      </Typography>
    </Stack>
  );
};

const UsageBar = ({ user }: { user: AdminUserUsage }) => {
  const k = user.tokens_by_kind;
  return (
    <Tooltip
      title={`Output ${formatTokens(k.output)} · input ${formatTokens(k.input)} · cache write ${formatTokens(k.cache_write)} (+${formatTokens(k.cache_write_1h)} for an hour) · cache read ${formatTokens(k.cache_read)}`}
    >
      <Box sx={{ minWidth: 140 }}>
        <Typography variant="body2">
          {formatTokens(user.tokens)} · {formatPercent(user.percent)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, user.percent)}
          color={user.blocked ? 'error' : user.percent >= 80 ? 'warning' : 'primary'}
          aria-label={`Share of the limit ${user.email ?? user.user_id} used`}
        />
      </Box>
    </Tooltip>
  );
};

/**
 * Every user's token usage of a month against their limit, for administrators: what it cost, how many apps it made,
 * and the limit, which they can change. Tokens are turned into dollars and apps at the month's own rates once it has
 * enough apps, and at a measured default before that.
 */
export const Admin = () => {
  const { api, isAdmin } = useAuth();
  const periods = recentPeriods(6);
  const [period, setPeriod] = useState(periods[0]);
  const [data, setData] = useState<ListUsageResponse>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      setData(await api.listUsage(period));
    } catch (caught) {
      setError(describe(caught, 'Could not load the usage.'));
    }
  }, [api, period]);

  useEffect(() => {
    if (isAdmin) {
      setData(undefined);
      void load();
    }
  }, [isAdmin, load]);

  if (!isAdmin) {
    return <Alert severity="info">Only administrators can see this page.</Alert>;
  }

  const save = async (user: AdminUserUsage, limit: number | null) => {
    setNotice(undefined);
    try {
      await api.updateUsageLimit(user.user_id, limit);
      setNotice(
        `${user.email ?? user.user_id}: ${limit === null ? 'back to the default limit' : `limit set to ${formatTokens(limit)} tokens`}.`
      );
      await load();
    } catch (caught) {
      setError(describe(caught, 'Could not change the limit.'));
    }
  };

  const guideline = data?.guideline;
  const users = data?.users ?? [];
  const tokens = users.reduce((s, u) => s + u.tokens, 0);
  const cost = users.reduce((s, u) => s + u.cost_usd, 0);
  const apps = users.reduce((s, u) => s + u.apps, 0);
  const atLimit = users.filter((u) => u.blocked).length;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 280 }}>
          Token usage of every user and their monthly limit. Every token the models read or write counts; when a user
          reaches their limit, the assistant stops taking their requests until the month ends or the limit is raised.
        </Typography>
        <TextField
          select
          size="small"
          label="Month"
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {periods.map((p) => (
            <MenuItem key={p} value={p}>
              {formatPeriod(p)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      )}
      {!data && !error && <LinearProgress aria-label="Loading the usage" />}

      {data && guideline && (
        <>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Figure label="Tokens" value={formatTokens(tokens)} detail={formatPeriod(data.period)} />
            <Figure label="Cost (estimated)" value={formatUsd(cost)} detail="Bedrock list price, by model" />
            <Figure label="Apps" value={String(apps)} detail="Projects the assistant worked on" />
            <Figure
              label="Users at their limit"
              value={`${atLimit} of ${users.length}`}
              detail={`Default limit ${formatTokens(data.default_limit_tokens)} tokens`}
            />
          </Stack>

          <Alert severity="info" icon={false}>
            Guideline: ≈ {formatUsd(guideline.usd_per_mtoken)} per million tokens and ≈{' '}
            {formatTokens(guideline.tokens_per_app)} tokens per app (a roadmap of a few steps),{' '}
            {guideline.basis === 'measured'
              ? "measured from this month's usage of all users."
              : 'an estimate from earlier runs until this month has enough apps of its own.'}
          </Alert>

          <TableContainer component={Paper}>
            <Table size="small" aria-label="Usage by user">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Used</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Apps</TableCell>
                  <TableCell>Monthly limit</TableCell>
                  <TableCell align="right">Left</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const left = Math.max(0, user.limit_tokens - user.tokens);
                  return (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography variant="body2">{user.email ?? `(removed) ${user.user_id}`}</Typography>
                          {user.admin && <Chip size="small" label="admin" />}
                          {user.blocked && <Chip size="small" color="error" label="stopped" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <UsageBar user={user} />
                      </TableCell>
                      <TableCell align="right">{formatUsd(user.cost_usd)}</TableCell>
                      <TableCell align="right">{user.apps}</TableCell>
                      <TableCell>
                        <LimitEditor
                          user={user}
                          guideline={guideline}
                          defaultLimit={data.default_limit_tokens}
                          onSave={(limit) => save(user, limit)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{formatTokens(left)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ≈ {tokensToApps(left, guideline)} apps
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>No users.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};
